import { useEffect, useRef, useState } from "react";
import type { ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Rechnung, Rechnungsvorschlag as Sicht } from "@kundeninfo/api-client";
import { Feld, Modal, ModalFuss, darfAbbrechen, useTelefon } from "@kundeninfo/ui";
import { Chip, Eingabe, Hinweis, Tabelle } from "@kundeninfo/ui/me-design";
import { formatBetrag } from "../format.js";
import { KiKnopf } from "./meHuellen.js";
import "./Rechnungsvorschlag.css";

/**
 * DAS VORSCHLAGS-MODAL am Auftragsdetail (Spec, „Oberfläche").
 *
 * **EIN Schema, EINE Tabelle**: keine getrennten Blöcke für „erste Rechnung" und
 * „Folgerechnung", sondern eine Liste der noch nicht voll abgerechneten Positionen je
 * Leistungsjahr — mit Auswahlkästchen, editierbarer Menge und editierbarem Betrag, dem
 * Herkunftshinweis des Servers und der Summe unten.
 *
 * **Striktes Modal** (Hausregel): Rahmen und Fußleiste kommen aus `@kundeninfo/ui`, es
 * gibt genau zwei Ausgänge, Escape geht über Abbrechen, und beide Wege sind gesperrt,
 * solange der POST läuft.
 *
 * **Gerechnet wird hier NICHT.** Menge und Betrag sind Eingaben des Bearbeiters und
 * gehen so, wie sie dastehen, an den Server; die Summe unten ist eine Addition der
 * gewählten Beträge und keine zweite Wahrheit — der Server rechnet die Rechnung.
 *
 * **Fehlerabbildung** (Ruling des Controllers, Paket Teilrechnungen): Eine 422 mit dem
 * Code POSTEN_RESERVIERT kann Feldbefunde als `zeilen[i].plannedChargeIds` ODER als
 * `plannedChargeIds` ohne Index tragen — der indizierte Befund steht an SEINER Zeile
 * (Spalte Herkunft), alles andere am Kopf. VORSCHLAG_UNGUELTIG und VORSCHLAG_LEER kommen
 * ohne indizierte Felder und stehen deshalb immer am Kopf. Eine 409 heisst „von jemand
 * anderem geändert" und lädt den Vorschlag neu — aus der Konfliktantwort, wenn sie einen
 * frischen Stand mitbringt, sonst über einen erneuten Abruf.
 *
 * **Eigener CSS-Block `rvs__`.**
 */

export type VorschlagsClient =
  Pick<ApiClient, "getRechnungsvorschlag" | "erzeugeRechnungAusVorschlag">;

export type RechnungsvorschlagProps = {
  client: VorschlagsClient;
  customerId: string;
  orderId: string;
  /** Der erzeugte Entwurf — der Aufrufer springt hinein. */
  onFertig: (rechnung: Rechnung) => void;
  onAbbrechen: () => void;
  /** Der Sprung in den Kasten „Abrechnungsquellen", wenn der Vorschlag wegen einer
   *  fehlenden Verknüpfung gesperrt ist (R6). Weglassbar: ohne ihn steht nur die Meldung. */
  onZuAbrechnungsquellen?: (() => void) | undefined;
};

type Zeilenentwurf = { gewaehlt: boolean; menge: string; betrag: string };

function entwurfAus(zeile: Sicht["zeilen"][number]): Zeilenentwurf {
  return {
    gewaehlt: true,
    menge: zeile.vorschlagsMenge === null ? "" : String(zeile.vorschlagsMenge),
    betrag: zeile.vorschlag,
  };
}

function entwuerfeAus(zeilen: Sicht["zeilen"]): Record<string, Zeilenentwurf> {
  const alle: Record<string, Zeilenentwurf> = {};
  for (const zeile of zeilen) alle[schluessel(zeile)] = entwurfAus(zeile);
  return alle;
}

function schluessel(zeile: Sicht["zeilen"][number]): string {
  return `${zeile.periodStart}|${zeile.orderItemId}`;
}

/** Eine Zahl aus einem Eingabefeld — ungültig heisst 0, nicht NaN: Eine Zeile mit
 *  unlesbarem Betrag soll nichts kaputt machen, sondern nichts beitragen. */
function zahlAus(text: string): number {
  const wert = Number(text.replace(",", "."));
  return Number.isFinite(wert) ? wert : 0;
}

/** Der indizierte Feldpfad eines POSTEN_RESERVIERT-Befundes — `zeilen[3].plannedChargeIds`. */
const ZEILENFELD_RE = /^zeilen\[(\d+)]\.plannedChargeIds$/;

/**
 * Ordnet die Feldbefunde einer 422 den betroffenen Zeilen zu (Ruling POSTEN_RESERVIERT):
 * ein indizierter Pfad geht an SEINE Zeile (Index innerhalb der GESENDETEN, also
 * gewählten Zeilen), alles andere — inklusive `plannedChargeIds` ohne Index — an den Kopf.
 */
function fehlerZuordnen(
  fehler: ApiError, gesendet: Sicht["zeilen"],
): { kopf: string | null; zeilen: Record<string, string> } {
  const zeilen: Record<string, string> = {};
  let kopf: string | null = null;
  for (const feld of fehler.fields ?? []) {
    const treffer = ZEILENFELD_RE.exec(feld.field);
    const zeile = treffer !== null ? gesendet[Number(treffer[1])] : undefined;
    if (zeile !== undefined) {
      zeilen[schluessel(zeile)] = feld.message;
    } else {
      kopf = kopf === null ? feld.message : `${kopf} ${feld.message}`;
    }
  }
  if (kopf === null && Object.keys(zeilen).length === 0) kopf = fehler.message;
  return { kopf, zeilen };
}

/**
 * Die Sätze der Sperre R6 (422 `ABRECHNUNGSQUELLE_FEHLT`): JEDER Eintrag aus `fields[]` —
 * der Server liefert einen je betroffener Rate (Review A8: nicht nur `message`, sonst sähe
 * man bei zwei gesperrten Raten nur eine). Ohne Befunde bleibt die Kopfmeldung, damit die
 * Sperre nie stumm ist.
 */
function sperrBefunde(fehler: ApiError): string[] {
  const saetze = (fehler.fields ?? []).map((f) => f.message);
  return saetze.length > 0 ? saetze : [fehler.message];
}

export function Rechnungsvorschlag(props: RechnungsvorschlagProps): ReactElement {
  const { client, customerId, orderId, onFertig, onAbbrechen } = props;
  const [stichtag, setStichtag] = useState<string>(
    () => new Date().toISOString().slice(0, 10));
  const [sicht, setSicht] = useState<Sicht | null>(null);
  const [entwuerfe, setEntwuerfe] = useState<Record<string, Zeilenentwurf>>({});
  const [fehler, setFehler] = useState<string | null>(null);
  const [zeilenFehler, setZeilenFehler] = useState<Record<string, string>>({});
  /** Die Befunde der Sperre R6 (422 ABRECHNUNGSQUELLE_FEHLT) — je betroffener Rate ein
   *  Satz. Solange sie stehen, bleibt der Vorschlag leer. */
  const [quellenSperre, setQuellenSperre] = useState<string[]>([]);
  const [laeuft, setLaeuft] = useState(false);
  /** Am Telefon (Entscheid 42) stehen Menge und Betrag nur lesend; bearbeitet wird im Dialog. */
  const telefon = useTelefon();
  /** Die Zeile im Bearbeitungsdialog: ihr Schlüssel, ihr Name und der Entwurf des Dialogs. */
  const [bearbeitung, setBearbeitung] =
    useState<{ key: string; name: string; menge: string; betrag: string } | null>(null);
  const lauf = useRef(0);
  const getVorschlag = client.getRechnungsvorschlag;

  useEffect(() => {
    const meiner = ++lauf.current;
    setSicht(null);
    setFehler(null);
    setZeilenFehler({});
    setQuellenSperre([]);
    getVorschlag(customerId, orderId, stichtag)
      .then((antwort) => {
        if (lauf.current !== meiner) return;
        setSicht(antwort);
        setEntwuerfe(entwuerfeAus(antwort.zeilen));
      })
      .catch((e: unknown) => {
        if (lauf.current !== meiner) return;
        if (e instanceof ApiError && e.code === "ABRECHNUNGSQUELLE_FEHLT") {
          setQuellenSperre(sperrBefunde(e));
          return;
        }
        setFehler("Der Rechnungsvorschlag konnte nicht geladen werden.");
      });
    return () => { lauf.current++; };
  }, [getVorschlag, customerId, orderId, stichtag]);

  function setze(key: string, teil: Partial<Zeilenentwurf>): void {
    setEntwuerfe((bisher) => ({ ...bisher, [key]: { ...bisher[key]!, ...teil } }));
  }

  const gewaehlte = (sicht?.zeilen ?? []).filter(
    (z) => entwuerfe[schluessel(z)]?.gewaehlt === true);
  const summe = gewaehlte.reduce(
    (s, z) => s + zahlAus(entwuerfe[schluessel(z)]!.betrag), 0);

  async function erzeugen(): Promise<void> {
    const ausgewaehlt = gewaehlte;
    // Der Stichtag im Antrag ist der VOM SERVER bestätigte (`sicht.stichtag`), nicht
    // notwendigerweise der aktuelle Text im Eingabefeld: Er gehört zu GENAU dem Vorschlag,
    // der gerade auf dem Bildschirm steht — ein Tippen im Feld löst ohnehin sofort einen
    // neuen Ladevorgang aus, der Vorschlag und Eingabe wieder in Deckung bringt.
    const stichtagDesVorschlags = sicht?.stichtag ?? stichtag;
    setLaeuft(true);
    setFehler(null);
    setZeilenFehler({});
    setQuellenSperre([]);
    try {
      const rechnung = await client.erzeugeRechnungAusVorschlag(customerId, orderId, {
        stichtag: stichtagDesVorschlags,
        zeilen: ausgewaehlt.map((z) => {
          const e = entwuerfe[schluessel(z)]!;
          return {
            periodStart: z.periodStart,
            orderItemId: z.orderItemId,
            quantity: e.menge.trim() === "" ? null : zahlAus(e.menge),
            netAmount: zahlAus(e.betrag),
            plannedChargeIds: z.plannedChargeIds,
          };
        }),
      });
      onFertig(rechnung);
    } catch (ursprung) {
      if (ursprung instanceof ApiError && ursprung.status === 409) {
        setFehler("Der Auftrag wurde von jemand anderem geändert. Der Rechnungsvorschlag "
          + "ist neu geladen.");
        const frisch = ursprung.conflict?.current as Sicht | undefined;
        if (frisch !== undefined) {
          setSicht(frisch);
          setEntwuerfe(entwuerfeAus(frisch.zeilen));
        } else {
          try {
            const neu = await client.getRechnungsvorschlag(customerId, orderId, stichtag);
            setSicht(neu);
            setEntwuerfe(entwuerfeAus(neu.zeilen));
          } catch { /* die Meldung steht schon; der alte Stand bleibt sichtbar */ }
        }
      } else if (ursprung instanceof ApiError && ursprung.status === 422) {
        if (ursprung.code === "ABRECHNUNGSQUELLE_FEHLT") {
          // Der Vorschlag bleibt leer, solange die Sperre steht (Fixrunde A10-1): Tabelle
          // und „Entwurf erzeugen" neben der Sperre wären eine Einladung zum Wiederholen.
          setSicht(null);
          setEntwuerfe({});
          setQuellenSperre(sperrBefunde(ursprung));
        } else {
          const { kopf, zeilen } = fehlerZuordnen(ursprung, ausgewaehlt);
          setFehler(kopf);
          setZeilenFehler(zeilen);
        }
      } else {
        setFehler(ursprung instanceof Error
          ? ursprung.message
          : "Der Rechnungsentwurf konnte nicht erzeugt werden.");
      }
    } finally {
      setLaeuft(false);
    }
  }

  /** „Speichern“ im Dialog übernimmt in den Vorschlag (kein Serverweg — der folgt erst mit
   *  „Entwurf erzeugen“). */
  function bearbeitungUebernehmen(): void {
    if (bearbeitung === null) return;
    setze(bearbeitung.key, { menge: bearbeitung.menge, betrag: bearbeitung.betrag });
    setBearbeitung(null);
  }

  async function bearbeitungAbbrechen(): Promise<void> {
    if (bearbeitung === null) return;
    const vorher = entwuerfe[bearbeitung.key];
    const geaendert = vorher === undefined || vorher.menge !== bearbeitung.menge
      || vorher.betrag !== bearbeitung.betrag;
    if (!(await darfAbbrechen(geaendert))) return;
    setBearbeitung(null);
  }

  async function abbrechen(): Promise<void> {
    if (laeuft) return;
    if (!(await darfAbbrechen(gewaehlte.length > 0))) return;
    onAbbrechen();
  }

  return (
    <Modal titel="Rechnung vorschlagen" breite="breit" onAbbrechen={abbrechen}
      abbrechenGesperrt={laeuft}>
      <div className="rvs">
        <div className="rvs__kopf">
          {/* me-eingabe (Art datum) — beschriftet sich selbst; jede Änderung lädt den
              Vorschlag zum neuen Stichtag (Effekt oben). */}
          <Feld id="rvs-stichtag" label="Stichtag" typ="date" bearbeitbar wert={stichtag}
            onChange={setStichtag} gesperrt={laeuft} />
        </div>

        {/* Schlechtestes oben (Hausregel): Sperre und Fehler vor der Tabelle. */}
        {quellenSperre.length > 0 && (
          <Hinweis art="fehler" className="rvs__quellen"
            {...(props.onZuAbrechnungsquellen !== undefined ? { aktionen: (
              <KiKnopf art="leise" className="rvs__quellen-sprung"
                onClick={props.onZuAbrechnungsquellen}>
                Zum Kasten Abrechnungsquellen
              </KiKnopf>
            ) } : {})}>
            <p className="rvs__quellen-titel">
              Der Vorschlag ist gesperrt — eine Abrechnungsquelle fehlt:
            </p>
            <ul className="rvs__quellen-liste">
              {quellenSperre.map((m, i) => <li key={i}>{m}</li>)}
            </ul>
          </Hinweis>
        )}
        {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
        {sicht === null && fehler === null && quellenSperre.length === 0
          && <p className="rvs__laedt">Der Vorschlag wird geladen …</p>}

        {sicht !== null && !sicht.hatLaufzeit && (
          <p className="rvs__hinweis">
            Ohne Laufzeitbeginn gibt es keine Leistungszeiträume — und damit nichts
            vorzuschlagen. Laufzeit im Auftrag nachtragen.
          </p>
        )}
        {sicht !== null && sicht.hatLaufzeit && sicht.zeilen.length === 0 && (
          <p className="rvs__hinweis">
            Zu diesem Stichtag ist nichts offen. Ein späterer Stichtag zeigt die nächsten
            fälligen Posten.
          </p>
        )}

        {sicht !== null && sicht.zeilen.length > 0 && (
          /* me-tabelle (ME-Etappe 3, Aufgabe 4): Rahmen, Kopfzeile, Zebra und das
             waagerechte Rollen bringt das Element. Menge und Betrag sind seit P12
             (Gesamtreview W2) `me-eingabe` ohne sichtbare Beschriftung (Name über
             `aria-label`, Muster `ZellFeld` im Positionseditor) und führen Text — gelesen wird
             erst beim Erzeugen. Das Kästchen „Übernehmen“ bleibt nativ: Übergang P4-3
             (me-design hat keine Kästchengruppe, Entscheid 40).
             `stapeln` (Entscheid 30, ME-Etappe 4 P8): Am Telefon wird jede Zeile eine Karte
             „Spaltenkopf · Wert“ — sieben Spalten mit drei Eingaben rollten dort seitwärts
             aus dem vollflächigen Dialog. */
          <Tabelle stapeln className="rvs__tabellenrahmen">
            <table className="rvs__tabelle">
              <thead>
                <tr>
                  <th scope="col">Übernehmen</th>
                  <th scope="col">Leistungsjahr</th>
                  <th scope="col">Position</th>
                  <th scope="col" data-ausrichtung="rechts">Offen</th>
                  <th scope="col" data-ausrichtung="rechts">Menge</th>
                  <th scope="col" data-ausrichtung="rechts">Betrag</th>
                  <th scope="col">Herkunft</th>
                  {telefon && (
                    <th scope="col"><span className="rvs__unsichtbar">Aktionen</span></th>
                  )}
                </tr>
              </thead>
              <tbody>
                {sicht.zeilen.map((zeile) => {
                  const key = schluessel(zeile);
                  const e = entwuerfe[key]!;
                  return (
                    <tr key={key}>
                      <td>
                        <input type="checkbox" checked={e.gewaehlt}
                          aria-label={`${zeile.name} übernehmen`}
                          onChange={(ev) => setze(key, { gewaehlt: ev.target.checked })} />
                      </td>
                      <td>{zeile.periodStart.slice(0, 4)}</td>
                      <td>
                        {zeile.name}
                        {zeile.customerOrderLine !== null && (
                          <span className="rvs__bestellposition">
                            {zeile.customerOrderLine}
                          </span>
                        )}
                        {zeile.billingProbability < 100 && (
                          <Chip className="rvs__pille">
                            Budget, {zeile.billingProbability} %
                          </Chip>
                        )}
                      </td>
                      <td className="ki-num" data-ausrichtung="rechts">
                        {formatBetrag(Number(zeile.offen), sicht.currency)}
                      </td>
                      {telefon ? (
                        <>
                          {/* Entscheid 42: am Telefon nur lesend (gestapelte Karte). */}
                          <td className="ki-num" data-ausrichtung="rechts">
                            {e.menge === "" ? "—" : e.menge}
                          </td>
                          <td className="ki-num" data-ausrichtung="rechts">
                            {formatBetrag(zahlAus(e.betrag), sicht.currency)}
                          </td>
                        </>
                      ) : (
                        <>
                          <td data-ausrichtung="rechts">
                            <Eingabe className="rvs__eingabe" inputMode="decimal" wert={e.menge}
                              aria-label={`Menge ${zeile.name}`}
                              onGeaendert={(d) => setze(key, { menge: d.wert })} />
                          </td>
                          <td data-ausrichtung="rechts">
                            <Eingabe className="rvs__eingabe" inputMode="decimal" wert={e.betrag}
                              aria-label={`Betrag ${zeile.name}`}
                              onGeaendert={(d) => setze(key, { betrag: d.wert })} />
                          </td>
                        </>
                      )}
                      <td className="rvs__herkunft">
                        {zeile.herkunft}
                        {zeilenFehler[key] !== undefined && (
                          <span className="rvs__zeilenfehler">{zeilenFehler[key]}</span>
                        )}
                      </td>
                      {telefon && (
                        <td data-label="">
                          <KiKnopf name={`${zeile.name} bearbeiten`} deaktiviert={laeuft}
                            onClick={() => setBearbeitung(
                              { key, name: zeile.name, menge: e.menge, betrag: e.betrag })}>
                            Bearbeiten
                          </KiKnopf>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row" colSpan={5}>Summe</th>
                  <td className="ki-num" data-ausrichtung="rechts" data-testid="rvs-summe">
                    {formatBetrag(summe, sicht.currency)}
                  </td>
                  <td />
                  {telefon && <td />}
                </tr>
              </tfoot>
            </table>
          </Tabelle>
        )}
      </div>
      {bearbeitung !== null && (
        /* Entscheid 42: der Bearbeitungsdialog der Zeile, gestapelt über dem Vorschlag
           (strikt, am Telefon vollflächig; Abbrechen fragt bei Änderungen nach). */
        <Modal titel={`${bearbeitung.name} bearbeiten`} gestapelt
          onAbbrechen={() => { void bearbeitungAbbrechen(); }}>
          <div className="rvs__dialog">
            <Eingabe beschriftung="Menge" inputMode="decimal" wert={bearbeitung.menge}
              onGeaendert={(d) => setBearbeitung((b) => (b === null ? b : { ...b, menge: d.wert }))} />
            <Eingabe beschriftung="Betrag" inputMode="decimal" wert={bearbeitung.betrag}
              einheit={sicht?.currency ?? "EUR"}
              onGeaendert={(d) => setBearbeitung((b) => (b === null ? b : { ...b, betrag: d.wert }))} />
          </div>
          <ModalFuss speichernText="Speichern" onSpeichern={bearbeitungUebernehmen}
            onAbbrechen={() => { void bearbeitungAbbrechen(); }} />
        </Modal>
      )}
      <ModalFuss speichernText="Entwurf erzeugen"
        speichernGesperrt={laeuft || gewaehlte.length === 0}
        abbrechenGesperrt={laeuft}
        onSpeichern={() => { void erzeugen(); }} onAbbrechen={abbrechen} />
    </Modal>
  );
}
